/** Bubble chat → append-only lên form BSC (không gọi API lưu). */
import {
  appendB1TextsToPage,
  B1_APPLY_TARGETS,
  detectB1ApplyTarget,
  extractAnswerBullets,
  textsFromCandidates,
  type B1ApplyCandidate,
  type B1ApplySection,
  type B1ApplyTarget,
} from './b1ApplyOffer';

export type BscApplySection =
  | B1ApplySection
  | 'strengths'
  | 'weaknesses'
  | 'opportunities'
  | 'threats'
  | 'strategy_so'
  | 'strategy_wo'
  | 'strategy_st'
  | 'strategy_wt'
  | 'strategy_chon'
  | 'strategy_ketqua'
  | 'b4_objective_theme'
  | 'b4_objective_company'
  | 'b5_bone'
  | 'b5_kpi'
  | 'b6_target';

export interface BscApplyTarget {
  section: BscApplySection;
  label: string;
  agents: string[];
  /** b1 … b5 */
  step: string;
}

export interface BscApplyOfferState {
  section: BscApplySection;
  label: string;
  texts: string[];
  /** Kèm theo sự kiện thêm vào trang (B3 kết quả `items`, B4 `aspects`) — từ trợ lý bước (page_append). */
  extra?: Record<string, unknown>;
  status: 'pending' | 'applying' | 'applied' | 'dismissed' | 'error';
  error?: string;
}

const B2_SWOT_TARGETS: Record<'strengths' | 'weaknesses' | 'opportunities' | 'threats', BscApplyTarget> = {
  strengths: { section: 'strengths', label: 'Điểm mạnh (SWOT)', agents: ['swot', 'competitive'], step: 'b2' },
  weaknesses: { section: 'weaknesses', label: 'Điểm yếu (SWOT)', agents: ['swot'], step: 'b2' },
  opportunities: { section: 'opportunities', label: 'Cơ hội (SWOT)', agents: ['swot'], step: 'b2' },
  threats: { section: 'threats', label: 'Thách thức (SWOT)', agents: ['swot'], step: 'b2' },
};

const B2_STRATEGY_TARGETS: Record<string, BscApplyTarget> = {
  strategy_so: { section: 'strategy_so', label: 'Chiến lược SO', agents: ['strategy'], step: 'b2' },
  strategy_wo: { section: 'strategy_wo', label: 'Chiến lược WO', agents: ['strategy'], step: 'b2' },
  strategy_st: { section: 'strategy_st', label: 'Chiến lược ST', agents: ['strategy'], step: 'b2' },
  strategy_wt: { section: 'strategy_wt', label: 'Chiến lược WT', agents: ['strategy'], step: 'b2' },
};

const B5_TARGETS: Record<'b5_bone' | 'b5_kpi', BscApplyTarget> = {
  b5_bone: { section: 'b5_bone', label: 'Xương cá (CSF) cho đầu cá', agents: ['fishbone'], step: 'b5' },
  b5_kpi: { section: 'b5_kpi', label: 'KPI cho đầu cá', agents: ['fishbone'], step: 'b5' },
};

const fold = (s: string) =>
  s
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/đ/g, 'd')
    .replace(/Đ/g, 'D')
    .toLowerCase();

function detectB2Target(userMessage: string, agents: string[], route?: string): BscApplyTarget | null {
  const onStrategy = route?.includes('chien-luoc') && !route?.includes('swot');
  const q = fold(userMessage);
  if (onStrategy || agents.includes('strategy')) {
    if (/\bso\b|strength.*opportun|diem manh.*co hoi/.test(q)) return B2_STRATEGY_TARGETS.strategy_so;
    if (/\bwo\b|weakness.*opportun|diem yeu.*co hoi/.test(q)) return B2_STRATEGY_TARGETS.strategy_wo;
    if (/\bst\b|strength.*threat|diem manh.*thach thuc/.test(q)) return B2_STRATEGY_TARGETS.strategy_st;
    if (/\bwt\b|weakness.*threat|diem yeu.*thach thuc/.test(q)) return B2_STRATEGY_TARGETS.strategy_wt;
    if (onStrategy || agents.includes('strategy')) return B2_STRATEGY_TARGETS.strategy_so;
  }
  if (/diem yeu|weakness|\bw\b/.test(q) && !/diem manh/.test(q)) return B2_SWOT_TARGETS.weaknesses;
  if (/co hoi|opportunit|\bo\b/.test(q) && !/thach thuc/.test(q)) return B2_SWOT_TARGETS.opportunities;
  if (/thach thuc|threat|\bt\b/.test(q)) return B2_SWOT_TARGETS.threats;
  if (/diem manh|strength|\bs\b/.test(q)) return B2_SWOT_TARGETS.strengths;
  if (agents.includes('swot')) return B2_SWOT_TARGETS.opportunities;
  return null;
}

export function detectBscApplyTarget(
  userMessage: string,
  agents: string[] = [],
  page?: { step?: string; route?: string },
): BscApplyTarget | null {
  const step = page?.step;
  const route = page?.route;
  if (step === 'b5') {
    if (!agents.includes('fishbone') && !/xuong|csf|kpi|chi tieu|yeu to thanh cong/i.test(fold(userMessage))) return null;
    return /\bkpi\b|chi tieu|do luong/i.test(fold(userMessage)) && !/xuong|csf|yeu to thanh cong/i.test(fold(userMessage))
      ? B5_TARGETS.b5_kpi
      : B5_TARGETS.b5_bone;
  }
  if (step === 'b4') {
    const onCompany = route?.includes('ban-do-chien-luoc') && !route?.includes('thanh-phan');
    if (agents.includes('strategy_map') || /muc tieu|ban do|phuong dien|nhan qua/i.test(fold(userMessage))) {
      return onCompany
        ? { section: 'b4_objective_company', label: 'Thêm mục tiêu bản đồ công ty', agents: ['strategy_map'], step: 'b4' }
        : { section: 'b4_objective_theme', label: 'Thêm mục tiêu bản đồ thành phần', agents: ['strategy_map'], step: 'b4' };
    }
  }
  if (step === 'b2') {
    const t = detectB2Target(userMessage, agents, route);
    if (t) return t;
  }
  if (step === 'b3' && route?.includes('lua-chon')) {
    if (agents.includes('strategy') || /chon|lua chon|uu tien/i.test(fold(userMessage))) {
      return { section: 'strategy_chon', label: 'Đánh dấu chọn chiến lược', agents: ['strategy'], step: 'b3' };
    }
  }
  if (step === 'b3' && route?.includes('ket-qua')) {
    if (agents.includes('strategy') || /ket qua|ky vong|muc tieu|result/i.test(fold(userMessage))) {
      return { section: 'strategy_ketqua', label: 'Thêm kết quả kỳ vọng', agents: ['strategy'], step: 'b3' };
    }
  }
  if (step === 'b1' || !step) {
    const b1 = detectB1ApplyTarget(userMessage, agents);
    if (b1) return { ...b1, step: 'b1' };
  }
  return null;
}

export function bscApplyTargetsForStep(step?: string): BscApplyTarget[] {
  if (step === 'b1') {
    return (Object.values(B1_APPLY_TARGETS) as B1ApplyTarget[]).map((t) => ({ ...t, step: 'b1' }));
  }
  if (step === 'b2') {
    return [...Object.values(B2_SWOT_TARGETS), ...Object.values(B2_STRATEGY_TARGETS)];
  }
  if (step === 'b3') {
    return [
      { section: 'strategy_chon', label: 'Gợi ý chọn chiến lược', agents: ['strategy'], step: 'b3' },
      { section: 'strategy_ketqua', label: 'Gợi ý kết quả chiến lược', agents: ['strategy'], step: 'b3' },
    ];
  }
  if (step === 'b4') {
    return [
      { section: 'b4_objective_theme', label: 'Mục tiêu bản đồ thành phần', agents: ['strategy_map'], step: 'b4' },
      { section: 'b4_objective_company', label: 'Mục tiêu bản đồ công ty', agents: ['strategy_map'], step: 'b4' },
    ];
  }
  if (step === 'b5') return Object.values(B5_TARGETS);
  if (step === 'b6') return [B6_TARGET];
  return [];
}

/** B6 · Đo lường chỉ số: điền chỉ tiêu cho KPI khớp tên trong bảng (chưa lưu — người dùng bấm Lưu). */
export const B6_TARGET: BscApplyTarget = { section: 'b6_target', label: 'Chỉ tiêu KPI (Bước 6)', agents: [], step: 'b6' };

export interface B6TargetItem {
  kpi: string;
  chi_tieu: string;
}

export const b6TargetText = (it: B6TargetItem) => `${it.kpi}: ${it.chi_tieu}`;

/** Thẻ đề xuất B6 từ candidates có cấu trúc (kpi, chi_tieu) của đề xuất AI. */
export function buildB6ApplyOffer(candidates: B1ApplyCandidate[]): BscApplyOfferState | undefined {
  const items: B6TargetItem[] = candidates
    .filter((c) => c.kpi?.trim() && c.chi_tieu?.trim())
    .map((c) => ({ kpi: String(c.kpi).trim(), chi_tieu: String(c.chi_tieu).trim() }));
  if (!items.length) return undefined;
  return { section: 'b6_target', label: B6_TARGET.label, texts: items.map(b6TargetText), extra: { items }, status: 'pending' };
}

export { textsFromCandidates, extractAnswerBullets, type B1ApplyCandidate };

/** `extra` (tuỳ chọn) gộp vào detail của sự kiện: B3 kết quả `items`, B4 `aspects` (song song với texts). */
export function appendBscTextsToPage(section: BscApplySection, texts: string[], extra: Record<string, unknown> = {}) {
  const clean = texts.map((t) => t.trim()).filter(Boolean);
  if (!clean.length) throw new Error('Không có nội dung để thêm.');
  if (
    section === 'industry_csf'
    || section === 'company_strengths'
    || section === 'competitor_strengths'
    || section === 'competitive_advantage'
    || section === 'competitors'
  ) {
    appendB1TextsToPage(section, clean);
    return;
  }
  if (section === 'strengths' || section === 'weaknesses' || section === 'opportunities' || section === 'threats') {
    window.dispatchEvent(new CustomEvent('bsc-ai-append-b2-swot', { detail: { section, texts: clean } }));
    return;
  }
  if (section.startsWith('strategy_') && section !== 'strategy_chon') {
    window.dispatchEvent(new CustomEvent('bsc-ai-append-b2-strategy', { detail: { section, texts: clean, ...extra } }));
    return;
  }
  if (section === 'strategy_chon') {
    window.dispatchEvent(new CustomEvent('bsc-ai-append-b3-chon', { detail: { texts: clean } }));
    return;
  }
  if (section === 'strategy_ketqua') {
    window.dispatchEvent(new CustomEvent('bsc-ai-append-b3-ketqua', { detail: { texts: clean, ...extra } }));
    return;
  }
  if (section === 'b4_objective_theme') {
    window.dispatchEvent(new CustomEvent('bsc-ai-append-b4-theme', { detail: { texts: clean, ...extra } }));
    return;
  }
  if (section === 'b4_objective_company') {
    window.dispatchEvent(new CustomEvent('bsc-ai-append-b4-company', { detail: { texts: clean, ...extra } }));
    return;
  }
  if (section === 'b5_bone' || section === 'b5_kpi') {
    // FishboneChatApplyBridge hỏi đầu cá đích rồi chuyển tiếp
    window.dispatchEvent(
      new CustomEvent('bsc-ai-append-b5', { detail: { kind: section === 'b5_bone' ? 'bone' : 'kpi', texts: clean } }),
    );
    return;
  }
  if (section === 'b6_target') {
    // Bảng Đo lường chỉ số (MeasureIndicators) nghe sự kiện, điền chỉ tiêu cho KPI khớp tên — chỉ các dòng đã chọn
    const items = ((extra.items as B6TargetItem[] | undefined) ?? []).filter((it) => clean.includes(b6TargetText(it)));
    if (!items.length) throw new Error('Không có chỉ tiêu để điền.');
    window.dispatchEvent(new CustomEvent('bsc-ai-append-b6', { detail: { items } }));
    return;
  }
  throw new Error(`Chưa hỗ trợ thêm vào section «${section}».`);
}
